import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useRef, useState, useCallback } from "react";
import Hls from "hls.js";
import { useLogger } from "../logger";
import { getSiteBaseURL } from "../ContentAPI";
import { useProfileAuthStore } from "../store/ProfileStore";
import { authUrl, useCacheBusterStore } from "../util/UrlUtils";
import { safeStringify } from "../util/SafeJson";
var PlayerInitializationState;
(function (PlayerInitializationState) {
    PlayerInitializationState["STATE_INITIALIZE"] = "STATE_INITIALIZE";
    PlayerInitializationState["STATE_CAN_PLAY"] = "STATE_CAN_PLAY";
    PlayerInitializationState["STATE_CANNOT_PLAY"] = "STATE_CANNOT_PLAY";
})(PlayerInitializationState || (PlayerInitializationState = {}));
const LOGPREFIX = "HLSVideoPlayer: ";
export const HLSVideoPlayer = ({ src, controls = false, autoplay = false, muted = true, loop = true, playsInline = true, preload = "metadata", style, className, cannotPlayImageUrl, poster, onClick, onError, onLoadedMetadata, onIntersectionChange, }) => {
    const videoRef = useRef(null);
    const hlsRef = useRef(null);
    const intersectionCleanupRef = useRef(null);
    const [isHLSSupported, setIsHLSSupported] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [playerInitState, setPlayerInitState] = useState(PlayerInitializationState.STATE_INITIALIZE);
    const logger = useLogger((state) => state);
    const auth = useProfileAuthStore();
    const cacheBusterStore = useCacheBusterStore();
    // Check HLS support
    useEffect(() => {
        const supported = Hls.isSupported();
        setIsHLSSupported(supported);
        logger.log(LOGPREFIX + "HLS supported: " + supported);
    }, []);
    // Initialize HLS or native video
    useEffect(() => {
        const video = videoRef.current;
        if (!video || hasError)
            return;
        // Clean up previous HLS instance
        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }
        // Clean up previous intersection observer
        if (intersectionCleanupRef.current) {
            intersectionCleanupRef.current();
            intersectionCleanupRef.current = null;
        }
        logger.log(LOGPREFIX + "Initializing video with src: " + src);
        // Check if URL contains .m3u8 in the pathname (ignore query parameters)
        const url = new URL(src);
        const isHLSUrl = url.pathname.includes(".m3u8");
        logger.log(LOGPREFIX + "Video src contains .m3u8: " + isHLSUrl);
        logger.log(LOGPREFIX + "HLS supported: " + isHLSSupported);
        if (isHLSSupported && isHLSUrl) {
            // Apply authentication to the HLS manifest URL
            const authorizedSrc = authUrl(src, cacheBusterStore, auth, logger);
            logger.log(LOGPREFIX + "Authorized HLS URL: " + authorizedSrc);
            // Extract bearer token and cache buster from authorized URL
            let bearerToken = null;
            let cacheBuster = null;
            try {
                const authUrl = new URL(authorizedSrc);
                bearerToken = authUrl.searchParams.get("bearer");
                cacheBuster = authUrl.searchParams.get("cb");
                logger.log(LOGPREFIX +
                    "Extracted bearer token: " +
                    (bearerToken ? "present" : "missing"));
                logger.log(LOGPREFIX +
                    "Extracted cache buster: " +
                    (cacheBuster ? cacheBuster : "missing"));
            }
            catch (e) {
                logger.log(LOGPREFIX + "Failed to extract bearer token and cache buster: " + e);
            }
            // Use HLS.js for HLS streams
            const hls = new Hls({
                enableWorker: true,
                lowLatencyMode: true,
                backBufferLength: 90,
                maxBufferLength: 30,
                maxBufferSize: 60 * 1000 * 1000,
                maxBufferHole: 0.5,
                maxFragLookUpTolerance: 0.25,
                maxStarvationDelay: 4,
                maxLoadingDelay: 4,
                appendErrorMaxRetry: 3,
                // CORS and network settings
                xhrSetup: (xhr, url) => {
                    xhr.withCredentials = false;
                    // Add bearer token and cache buster to all HLS segment and manifest requests
                    if ((bearerToken || cacheBuster) && url) {
                        try {
                            const urlObj = new URL(url);
                            // Add bearer token and cache buster to HLS requests (.m3u8 and .ts files)
                            if (urlObj.pathname.endsWith(".m3u8") ||
                                urlObj.pathname.endsWith(".ts")) {
                                if (bearerToken) {
                                    urlObj.searchParams.set("bearer", bearerToken);
                                }
                                if (cacheBuster) {
                                    urlObj.searchParams.set("cb", cacheBuster);
                                }
                                const newUrl = urlObj.toString();
                                logger.log(LOGPREFIX +
                                    "Adding bearer and cache buster to HLS request: " +
                                    newUrl);
                                // Store both bearer token and cache buster for use when request is opened
                                const xhrWithParams = xhr;
                                if (bearerToken) {
                                    xhrWithParams._hlsBearerToken = bearerToken;
                                }
                                if (cacheBuster) {
                                    xhrWithParams._hlsCacheBuster = cacheBuster;
                                }
                            }
                        }
                        catch (e) {
                            logger.log(LOGPREFIX +
                                "Failed to process URL for bearer token and cache buster: " +
                                e);
                        }
                    }
                    // Override the open method to add bearer token and cache buster to URL
                    const originalOpen = xhr.open.bind(xhr);
                    xhr.open = function (method, url, async = true, username, password) {
                        const xhrParams = this;
                        const xhrToken = xhrParams._hlsBearerToken;
                        const xhrCacheBuster = xhrParams._hlsCacheBuster;
                        if ((xhrToken || xhrCacheBuster) && typeof url === "string") {
                            try {
                                const urlObj = new URL(url);
                                if (urlObj.pathname.endsWith(".m3u8") ||
                                    urlObj.pathname.endsWith(".ts")) {
                                    if (xhrToken) {
                                        urlObj.searchParams.set("bearer", xhrToken);
                                    }
                                    if (xhrCacheBuster) {
                                        urlObj.searchParams.set("cb", xhrCacheBuster);
                                    }
                                    url = urlObj.toString();
                                    logger.log(LOGPREFIX +
                                        "Bearer token and cache buster added to URL: " +
                                        url);
                                }
                            }
                            catch (e) {
                                logger.log(LOGPREFIX +
                                    "Failed to add bearer and cache buster to URL in open: " +
                                    e);
                            }
                        }
                        return originalOpen.call(this, method, url, async, username, password);
                    };
                },
            });
            hls.loadSource(authorizedSrc);
            hls.attachMedia(video);
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                logger.log(LOGPREFIX + "HLS manifest parsed successfully");
                if (autoplay) {
                    video.play().catch((error) => {
                        logger.log(LOGPREFIX + "HLS autoplay failed: " + error);
                    });
                }
            });
            hls.on(Hls.Events.MANIFEST_LOADING, (_, data) => {
                logger.log(LOGPREFIX + "HLS manifest loading: " + data.url);
            });
            hls.on(Hls.Events.MANIFEST_LOADED, () => {
                logger.log(LOGPREFIX + "HLS manifest loaded successfully");
            });
            hls.on(Hls.Events.LEVEL_LOADED, (_, data) => {
                logger.log(LOGPREFIX + "HLS level loaded: " + data.details.url);
            });
            hls.on(Hls.Events.FRAG_LOADING, (_, data) => {
                logger.log(LOGPREFIX + "HLS fragment loading: " + data.frag.url);
            });
            hls.on(Hls.Events.FRAG_LOADED, () => {
                logger.log(LOGPREFIX + "HLS fragment loaded successfully");
            });
            hls.on(Hls.Events.FRAG_PARSING_INIT_SEGMENT, () => {
                logger.log(LOGPREFIX + "HLS fragment parsing init segment");
            });
            hls.on(Hls.Events.FRAG_PARSING_USERDATA, () => {
                logger.log(LOGPREFIX + "HLS fragment parsing userdata");
            });
            hls.on(Hls.Events.FRAG_PARSED, () => {
                logger.log(LOGPREFIX + "HLS fragment parsed");
            });
            hls.on(Hls.Events.BUFFER_APPENDING, () => {
                logger.log(LOGPREFIX + "HLS buffer appending");
            });
            hls.on(Hls.Events.BUFFER_APPENDED, () => {
                logger.log(LOGPREFIX + "HLS buffer appended");
            });
            hls.on(Hls.Events.ERROR, (_, data) => {
                // hls.js fires ERROR events routinely during healthy playback
                // (buffer stalls, fragment retries). Only fatal ones are failures;
                // non-fatal ones are logged with wording that avoids the daily
                // failure scan's lexical triggers (Error/Failed/Exception).
                if (data.fatal) {
                    // hls.js ErrorData carries networkDetails (the XMLHttpRequest) and
                    // frag, so it is cyclic; JSON.stringify would throw here.
                    logger.log(LOGPREFIX + "HLS error: " + safeStringify(data));
                    logger.log(LOGPREFIX + "HLS error type: " + data.type);
                    logger.log(LOGPREFIX + "HLS error details: " + data.details);
                    if (data.response) {
                        logger.log(LOGPREFIX + "HLS error response: " + safeStringify(data.response));
                    }
                    if (data.url) {
                        logger.log(LOGPREFIX + "HLS error URL: " + data.url);
                    }
                }
                if (data.fatal) {
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            logger.log(LOGPREFIX + "Fatal network error, trying to recover...");
                            setPlayerInitState(PlayerInitializationState.STATE_CANNOT_PLAY);
                            setTimeout(() => {
                                logger.log(LOGPREFIX + "Reloading HLS source after network error");
                                hls.startLoad(); // Retry loading
                            }, 2000);
                            break;
                        case Hls.ErrorTypes.MEDIA_ERROR:
                            logger.log(LOGPREFIX + "Fatal media error");
                            setPlayerInitState(PlayerInitializationState.STATE_CANNOT_PLAY);
                            setHasError(true);
                            if (onError) {
                                onError(data);
                            }
                            break;
                        default:
                            logger.error(LOGPREFIX +
                                "Fatal error, cannot recover - propagating to parent");
                            setPlayerInitState(PlayerInitializationState.STATE_CANNOT_PLAY);
                            setHasError(true);
                            if (onError) {
                                onError(data);
                            }
                            break;
                    }
                }
                else {
                    // Non-fatal - log (with scan-neutral wording) and continue
                    logger.log(LOGPREFIX +
                        "HLS non-fatal event: " +
                        data.type +
                        " - " +
                        data.details);
                }
            });
            hlsRef.current = hls;
        }
        else {
            // Use native video for non-HLS or when HLS.js is not supported
            logger.log(LOGPREFIX + "Using native video playback");
            video.src = src;
        }
        // Set up intersection observer for autoplay
        if (onIntersectionChange) {
            onIntersectionChange(video);
        }
        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
            if (intersectionCleanupRef.current) {
                intersectionCleanupRef.current();
                intersectionCleanupRef.current = null;
            }
        };
    }, [src, isHLSSupported, autoplay, hasError, onIntersectionChange]);
    // Add debugging for video element events
    useEffect(() => {
        const video = videoRef.current;
        if (!video)
            return;
        const handleLoadStart = () => {
            logger.log(LOGPREFIX + "Video loadstart event for: " + src);
        };
        const handleCanPlay = () => {
            logger.log(LOGPREFIX + "Video canplay event for: " + src);
            setPlayerInitState(PlayerInitializationState.STATE_CAN_PLAY);
        };
        const handleError = (event) => {
            logger.log(LOGPREFIX + "Video element error event for: " + src);
            // Don't stringify the event object as it has circular references
            const target = event.target;
            if (target && target.error) {
                logger.log(LOGPREFIX + "Video element error code: " + target.error.code);
                logger.log(LOGPREFIX + "Video element error message: " + target.error.message);
            }
            onError === null || onError === void 0 ? void 0 : onError(event);
            setHasError(true);
        };
        video.addEventListener("loadstart", handleLoadStart);
        video.addEventListener("canplay", handleCanPlay);
        video.addEventListener("error", handleError);
        return () => {
            video.removeEventListener("loadstart", handleLoadStart);
            video.removeEventListener("canplay", handleCanPlay);
            video.removeEventListener("error", handleError);
        };
    }, [src, onError]);
    // Handle video error - propagate to parent
    const handleVideoError = useCallback((event) => {
        var _a, _b;
        logger.log(LOGPREFIX + "Video error occurred with src: " + src);
        // Don't stringify the event object as it has circular references
        logger.log(LOGPREFIX + "Video error type: " + event.type);
        logger.log(LOGPREFIX + "Video error isTrusted: " + event.isTrusted);
        if (event.target instanceof HTMLVideoElement) {
            logger.log(LOGPREFIX + "Video error code: " + ((_a = event.target.error) === null || _a === void 0 ? void 0 : _a.code));
            logger.log(LOGPREFIX + "Video error message: " + ((_b = event.target.error) === null || _b === void 0 ? void 0 : _b.message));
            logger.log(LOGPREFIX + "Video readyState: " + event.target.readyState);
            logger.log(LOGPREFIX + "Video networkState: " + event.target.networkState);
        }
        setHasError(true);
        if (onError) {
            logger.log(LOGPREFIX + "Calling onError callback");
            onError(event);
        }
        else {
            logger.log(LOGPREFIX + "No onError callback provided");
        }
    }, [src, onError]);
    // Handle loaded metadata
    const handleLoadedMetadata = useCallback(() => {
        logger.log(LOGPREFIX + "Video metadata loaded for: " + src);
        if (onLoadedMetadata) {
            onLoadedMetadata();
        }
    }, [src, onLoadedMetadata]);
    logger.log(LOGPREFIX + ":render: state = " + playerInitState);
    // If player init state is CANNOT_PLAY, render video unsupported image
    if (playerInitState === PlayerInitializationState.STATE_CANNOT_PLAY) {
        return (_jsx("img", { onClick: onClick, src: cannotPlayImageUrl
                ? cannotPlayImageUrl
                : getSiteBaseURL() + "img/video-unplayable.jpg", style: {
                display: "flex",
                justifyContent: "center",
                width: "100%",
                cursor: "pointer",
            } }));
    }
    return (_jsx("video", { ref: videoRef, controls: controls, muted: muted, loop: loop, playsInline: playsInline, preload: preload, poster: poster, style: style, className: className, onClick: onClick, onError: handleVideoError, onLoadedMetadata: handleLoadedMetadata }));
};
